import React, { useEffect, useState } from 'react';
import { TrendingUp, Package, Calendar, CheckCircle, Bell, Activity, Users, Share2, Clock, ArrowUp, ArrowDown, Sparkles, Send, Rocket, Plus, Zap } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import fetchWithAuth from '../utils/api';
// AI Usage Widget temporairement désactivé : endpoint /api/ai/usage non disponible
// import AIUsageWidget from '../components/analytics/AIUsageWidget';
import { EmptyState } from '../components/ui/empty-state';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Dashboard = () => {
  const { t } = useTranslation('dashboard');
  const [stats, setStats] = useState({
    totalProducts: 0,
    scheduledPosts: 0,
    publishedPosts: 0,
    socialAccounts: 0,
    todayPosts: 0,
    engagement: 0
  });
  const [recentPosts, setRecentPosts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [activities, setActivities] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      const [productsRes, postsRes, accountsRes] = await Promise.all([
        fetchWithAuth('/api/products'),
        fetchWithAuth('/api/posts'),
        fetchWithAuth('/api/social-accounts')
      ]);

      // Parse responses
      const productsData = await productsRes.json();
      const postsData = await postsRes.json();
      const accountsData = await accountsRes.json();

      // Safeguard: ensure posts is an array
      const posts = Array.isArray(postsData) ? postsData : (postsData.posts || []);
      
      const today = new Date().toDateString();
      const todayPosts = posts.filter(p => 
        new Date(p.createdAt).toDateString() === today
      ).length;

      const scheduledCount = posts.filter(p => p.status === 'scheduled').length;
      const publishedCount = posts.filter(p => p.status === 'published').length;

      // Calculate engagement
      const totalEngagement = posts.reduce((acc, post) => {
        const analytics = post.analytics || {};
        return acc + (analytics.likes || 0) + (analytics.shares || 0) + (analytics.comments || 0);
      }, 0);

      setStats({
        totalProducts: productsData.products?.length || productsData.total || 0,
        scheduledPosts: scheduledCount,
        publishedPosts: publishedCount,
        socialAccounts: accountsData.accounts?.length || accountsData.total || 0,
        todayPosts,
        engagement: totalEngagement
      });

      // Recent posts
      setRecentPosts(posts.slice(0, 5));

      // Mock notifications
      setNotifications([
        { id: 1, type: 'success', message: 'Publication réussie sur Instagram', time: '5 min' },
        { id: 2, type: 'warning', message: '3 publications en attente', time: '1h' },
        { id: 3, type: 'info', message: 'Nouveau compte Facebook connecté', time: '2h' }
      ]);

      // Mock activities
      setActivities([
        { id: 1, action: t('activities.publication_created'), user: t('activities.you'), time: t('activities.time_ago.minutes', { count: 10 }), icon: Send },
        { id: 2, action: t('activities.account_connected'), user: t('activities.you'), time: t('activities.time_ago.hour', { count: 1 }), icon: Share2 },
        { id: 3, action: t('activities.product_added'), user: t('activities.you'), time: t('activities.time_ago.hours', { count: 2 }), icon: Package },
        { id: 4, action: t('activities.ai_content_generated'), user: t('activities.you'), time: t('activities.time_ago.hours', { count: 3 }), icon: Sparkles }
      ]);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    }
  };

  const statCards = [
    { 
      label: t('stats.posts_today'), 
      value: stats.todayPosts, 
      icon: Calendar, 
      color: 'blue',
      change: '+12%',
      trend: 'up',
      testId: 'stat-today-posts'
    },
    { 
      label: t('stats.scheduled_posts'), 
      value: stats.scheduledPosts, 
      icon: Clock, 
      color: 'violet',
      change: '+5%',
      trend: 'up',
      testId: 'stat-scheduled'
    },
    { 
      label: t('stats.connected_networks'), 
      value: stats.socialAccounts, 
      icon: Share2, 
      color: 'emerald',
      change: '+2',
      trend: 'up',
      testId: 'stat-accounts'
    },
    { 
      label: t('stats.total_engagement'), 
      value: stats.engagement, 
      icon: TrendingUp, 
      color: 'amber',
      change: '+18%',
      trend: 'up',
      testId: 'stat-engagement'
    },
  ];

  return (
    <div data-testid="dashboard-page" className="space-y-6 animate-slide-in">
      {/* Header */}
      <div>
        <div className="flex items-center space-x-3 mb-2">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-white">{t('title')}</h1>
          <div className="px-3 py-1 rounded-full bg-blue-600/10 border border-blue-500/20">
            <span className="text-sm font-semibold text-blue-400">{t('live_badge')}</span>
          </div>
        </div>
        <p className="text-base text-zinc-400">{t('subtitle')}</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, idx) => {
          const Icon = stat.icon;
          const TrendIcon = stat.trend === 'up' ? ArrowUp : ArrowDown;
          return (
            <div
              key={idx}
              data-testid={stat.testId}
              className="relative group"
            >
              <div className="absolute inset-0 gradient-primary rounded-2xl opacity-0 group-hover:opacity-10 transition-opacity blur-xl"></div>
              <div className="relative bg-[#111118] border border-zinc-800/50 rounded-2xl p-6 card-hover">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                    stat.color === 'blue' ? 'bg-blue-600/10 border border-blue-500/20' :
                    stat.color === 'violet' ? 'bg-violet-600/10 border border-violet-500/20' :
                    stat.color === 'emerald' ? 'bg-emerald-600/10 border border-emerald-500/20' :
                    'bg-amber-600/10 border border-amber-500/20'
                  }`}>
                    <Icon className={`w-6 h-6 ${
                      stat.color === 'blue' ? 'text-blue-400' :
                      stat.color === 'violet' ? 'text-violet-400' :
                      stat.color === 'emerald' ? 'text-emerald-400' :
                      'text-amber-400'
                    }`} />
                  </div>
                  <div className={`flex items-center space-x-1 px-2 py-1 rounded-lg ${
                    stat.trend === 'up' ? 'bg-emerald-600/10 text-emerald-400' : 'bg-red-600/10 text-red-400'
                  }`}>
                    <TrendIcon className="w-3 h-3" />
                    <span className="text-xs font-semibold">{stat.change}</span>
                  </div>
                </div>
                <div>
                  <p className="text-3xl font-bold text-white mb-1">{stat.value}</p>
                  <p className="text-sm text-zinc-500 font-medium">{stat.label}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* AI Usage Widget temporairement désactivé : endpoint /api/ai/usage non disponible */}
      {/* <AIUsageWidget /> */}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Notifications */}
        <div className="lg:col-span-2 bg-[#111118] border border-zinc-800/50 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-xl bg-blue-600/10 border border-blue-500/20 flex items-center justify-center">
                <Bell className="w-5 h-5 text-blue-400" />
              </div>
              <h2 className="text-xl font-semibold text-white">Notifications</h2>
            </div>
            <span className="text-sm text-zinc-500">{notifications.length} nouvelles</span>
          </div>
          <div className="space-y-3">
            {notifications.map((notif) => (
              <div
                key={notif.id}
                className="flex items-start space-x-3 p-4 rounded-xl bg-zinc-900/50 border border-zinc-800/50 hover:border-zinc-700/50 transition-colors"
              >
                <div className={`w-2 h-2 rounded-full mt-2 ${
                  notif.type === 'success' ? 'bg-emerald-400' :
                  notif.type === 'warning' ? 'bg-amber-400' :
                  'bg-blue-400'
                }`}></div>
                <div className="flex-1">
                  <p className="text-sm text-white font-medium">{notif.message}</p>
                  <p className="text-xs text-zinc-500 mt-1">{notif.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mini Calendar */}
        <div className="bg-[#111118] border border-zinc-800/50 rounded-2xl p-6">
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-violet-600/10 border border-violet-500/20 flex items-center justify-center">
              <Calendar className="w-5 h-5 text-violet-400" />
            </div>
            <h2 className="text-xl font-semibold text-white">Calendrier</h2>
          </div>
          <div className="space-y-3">
            <div className="text-center py-8">
              <p className="text-5xl font-bold text-white mb-1">{new Date().getDate()}</p>
              <p className="text-sm text-zinc-400 font-medium">
                {format(new Date(), 'MMMM yyyy', { locale: fr })}
              </p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-900/50 border border-zinc-800/50">
                <span className="text-sm text-zinc-300">Posts programmés</span>
                <span className="text-sm font-semibold text-white">{stats.scheduledPosts}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-zinc-900/50 border border-zinc-800/50">
                <span className="text-sm text-zinc-300">Posts aujourd'hui</span>
                <span className="text-sm font-semibold text-white">{stats.todayPosts}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Activity Feed */}
      <div className="bg-[#111118] border border-zinc-800/50 rounded-2xl p-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-emerald-600/10 border border-emerald-500/20 flex items-center justify-center">
            <Activity className="w-5 h-5 text-emerald-400" />
          </div>
          <h2 className="text-xl font-semibold text-white">Activité Récente</h2>
        </div>
        <div className="space-y-4">
          {activities.map((activity, idx) => {
            const Icon = activity.icon;
            return (
              <div key={activity.id} className="flex items-center space-x-4">
                <div className="flex-shrink-0">
                  <div className="w-10 h-10 rounded-xl bg-zinc-900/50 border border-zinc-800/50 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-zinc-400" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white">{activity.action}</p>
                  <p className="text-xs text-zinc-500">{activity.user} • {activity.time}</p>
                </div>
                {idx < activities.length - 1 && (
                  <div className="absolute left-8 top-12 w-px h-8 bg-zinc-800/50"></div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Posts */}
      <div className="bg-[#111118] border border-zinc-800/50 rounded-2xl p-6">
        <h2 className="text-2xl font-semibold text-white mb-6">Publications Récentes</h2>
        {recentPosts.length === 0 ? (
          <EmptyState
            icon={<Rocket size={56} />}
            title="Bienvenue sur NEXORA AI 🚀"
            message="Votre tableau de bord se remplira automatiquement dès votre première publication. Prêt à commencer ?"
            primaryAction={{
              label: "Créer ma première publication",
              href: "/app/publish",
              icon: <Plus size={20} />
            }}
            secondaryAction={{
              label: "Explorer les fonctionnalités IA",
              href: "/app/ai-profiles",
              variant: "outline",
              icon: <Zap size={20} />
            }}
            variant="success"
            size="large"
            animated={true}
            testId="empty-dashboard"
          />
        ) : (
          <div className="space-y-3" data-testid="recent-posts-list">
            {recentPosts.map((post) => (
              <div
                key={post.id}
                data-testid={`post-item-${post.id}`}
                className="flex items-center justify-between p-4 rounded-xl bg-zinc-900/50 border border-zinc-800/50 hover:border-zinc-700/50 transition-all card-hover"
              >
                <div className="flex-1">
                  <p className="text-white font-medium mb-1">{post.caption.substring(0, 60)}...</p>
                  <p className="text-sm text-zinc-500">{post.platform} • {post.status}</p>
                </div>
                <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  post.status === 'published' ? 'bg-emerald-600/10 text-emerald-400 border border-emerald-500/20' :
                  post.status === 'scheduled' ? 'bg-violet-600/10 text-violet-400 border border-violet-500/20' :
                  'bg-zinc-600/10 text-zinc-400 border border-zinc-500/20'
                }`}>
                  {post.status}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
